/**
 * What an agent is doing, read from a tool call's ACP kind and title.
 *
 * The same classification drives three surfaces: the Teams-style transcript
 * (chips under an agent's message), the agent cards (the "now" line) and
 * the pixel-office overlay. Titles are whatever the runtime sends — a
 * Copilot MCP call reads "mju-taskboard: task_move", a Claude Code one
 * "mcp__mju_taskboard__task_move", a file read "Read src/x.ts" — so the
 * match is on stems, not exact names.
 */
const MEMORY = /memory_(query|explain|path|search|status|update|save_result)|mjuMemory|graphify|knowledge.?graph/i;
const BOARD = /task_(start|done|blocked|delegate|list|get|create|move|approve|revise)|plan_submit|mjuTask|taskboard|task.?board/i;
const AGENTS = /agent_(send|broadcast|inbox)|mjuAgent|mjuMessage|\bmessage (an?|the) agent|broadcast/i;
const WEB = /\b(fetch|http|web|browse|curl|search the web|websearch)\b/i;
const SHELL = /\b(bash|shell|exec|execute|terminal|powershell|npm|pnpm|yarn|dotnet|git|make|run)\b/i;
const EDIT = /\b(edit|write|create|patch|replace|delete|move|rename|mkdir)\b/i;
const READ = /\b(read|grep|glob|search|view|cat|list|find|ls)\b/i;
/** Classify by the ACP `kind` first (read/edit/execute/fetch/think/search/…), then by the title. */
export function classifyActivity(kind, title) {
    const t = title ?? '';
    if (MEMORY.test(t))
        return { kind: 'memory', verb: 'checked memory', doing: 'checking memory' };
    if (BOARD.test(t))
        return { kind: 'board', verb: 'updated the task board', doing: 'working the task board' };
    if (AGENTS.test(t)) {
        const to = /(?:to|@)\s*"?([A-Z][\w-]+)/.exec(t)?.[1];
        return { kind: 'agents', verb: to ? `messaged ${to}` : 'messaged a teammate', doing: to ? `messaging ${to}` : 'messaging a teammate' };
    }
    switch (kind) {
        case 'think':
            return { kind: 'think', verb: 'thought it through', doing: 'thinking' };
        case 'read':
        case 'search':
            return { kind: 'read', verb: 'read code', doing: 'reading' };
        case 'edit':
        case 'delete':
        case 'move':
            return { kind: 'edit', verb: 'edited files', doing: 'editing' };
        case 'execute':
            return { kind: 'shell', verb: 'ran a command', doing: 'running a command' };
        case 'fetch':
            return { kind: 'web', verb: 'fetched a page', doing: 'fetching' };
        default:
            break;
    }
    if (WEB.test(t))
        return { kind: 'web', verb: 'fetched a page', doing: 'fetching' };
    if (SHELL.test(t))
        return { kind: 'shell', verb: 'ran a command', doing: 'running a command' };
    if (EDIT.test(t))
        return { kind: 'edit', verb: 'edited files', doing: 'editing' };
    if (READ.test(t))
        return { kind: 'read', verb: 'read code', doing: 'reading' };
    return { kind: 'other', verb: 'used a tool', doing: 'working' };
}
/** A short, human label for a tool title (strip MCP prefixes, keep the interesting tail). */
export function shortToolTitle(title, max = 48) {
    const cleaned = title
        .replace(/^mcp__[a-z0-9_-]+__/i, '')
        .replace(/^[a-z0-9-]+:\s*/i, '')
        .replace(/_/g, ' ')
        .trim();
    return cleaned.length > max ? `${cleaned.slice(0, max - 1)}…` : cleaned;
}
const PATH = /(?:[\w@~.-]+[\\/])*[\w@~.-]+\.[A-Za-z0-9]{1,8}\b/g;
const SHELL_PREFIX = /^(?:run(?:ning)?|execute|exec|bash|shell|terminal|powershell|pwsh|cmd|command)\b\s*(?:command)?\s*[:(]?\s*/i;
const VERB_PREFIX = /^(?:read(?:ing)?|view(?:ing)?|cat|open(?:ing)?|edit(?:ing)?|write|writing|create|creating|patch(?:ing)?|update|updating|delete|deleting|fetch(?:ing)?|get|search(?:ing)?|grep|glob|find|list|ls)\b\s*:?\s*/i;
function lastPath(text) {
    const all = text.match(PATH);
    const hit = all?.filter((p) => !/^\d+(\.\d+)*$/.test(p)).pop();
    if (!hit)
        return undefined;
    const base = hit.split(/[\\/]/).pop();
    return base || undefined;
}
function clip(text, max) {
    const one = text.split('\n')[0].trim();
    return one.length > max ? `${one.slice(0, max - 1)}…` : one;
}
/** What a call acted on, in a few words. */
export function toolTarget(kind, title) {
    const t = title.replace(/`/g, '').replace(/^mcp__[a-z0-9_-]+__/i, '').trim();
    if (!t)
        return undefined;
    switch (kind) {
        case 'shell': {
            const cmd = t.replace(SHELL_PREFIX, '').replace(/^\$\s*/, '').replace(/\)\s*$/, '').trim();
            return cmd ? clip(cmd, 48) : undefined;
        }
        case 'read':
        case 'edit':
            return lastPath(t) ?? (t.replace(VERB_PREFIX, '').trim() ? clip(t.replace(VERB_PREFIX, ''), 40) : undefined);
        case 'web': {
            const url = /https?:\/\/([^/\s]+)/.exec(t)?.[1];
            if (url)
                return url.replace(/^www\./, '');
            const rest = t.replace(VERB_PREFIX, '').trim();
            return rest ? clip(rest, 40) : undefined;
        }
        case 'other':
            return shortToolTitle(t, 40) || undefined;
        default:
            return undefined;
    }
}
/** What a tool step is, as the clause after "is": lower case, present tense ("running a command: npm test"). Chat's working strip and the agent cards say it the same way (D358, D359). */
export function doingOfStep(step) {
    const t = step.target;
    switch (step.kind) {
        case 'shell':
            return t ? `running a command: ${t}` : 'running a command';
        case 'read':
            return t ? `reading ${t}` : 'reading files';
        case 'edit':
            return t ? `editing ${t}` : 'editing files';
        case 'web':
            return t ? `fetching ${t}` : 'fetching a page';
        case 'think':
            return 'thinking';
        case 'memory':
            return 'checking memory';
        case 'board':
            return 'updating the task board';
        case 'agents':
            return 'messaging a teammate';
        default:
            return t ? `using ${t}` : 'working';
    }
}
/**
 * A step the host reported (an ACP tool call: its kind and title) in the working strip's words: "running a command: npm test",
 * "reading App.tsx". The Agents pane's cards, the Office hover, the Home feed and the node's terminal screens say a step this way (D359).
 */
export function describeAgentStep(step) {
    const info = classifyActivity(step.kind, step.title);
    return doingOfStep({ kind: info.kind, target: toolTarget(info.kind, step.title), title: step.title, status: step.status === 'completed' ? 'done' : 'active', verb: info.verb, doing: info.doing });
}
//# sourceMappingURL=toolWords.js.map